import React, { useEffect, useState } from 'react';
import type { FC } from 'react';

import { Button } from '../../ui/Button/Button';
import { DateRangePicker, formatRuDate, toIsoDate, type DateRange } from '../../ui/DatePicker';
import { Modal, ModalActions, ModalNote } from '../../ui/Modal/Modal';
import { Switch } from '../../ui/Switch/Switch';
import { IconDownload } from '../../../assets/icons/icons';
import { cx } from '../../../utils/cx';
import { downloadFile } from '../../../shared/api/download';
import { catalogExportPath, type CatalogExportKind } from '../../../entities/productAnalogs/api/productAnalogsApi';
import styles from './ExportCatalogModal.module.scss';

const KINDS: { value: CatalogExportKind; label: string; title: string }[] = [
    { value: 'all', label: 'Товары и аналоги', title: 'Справочник товаров и аналогов ЯНАО' },
    { value: 'products', label: 'Только товары', title: 'Справочник товаров ЯНАО' },
    { value: 'analogs', label: 'Только аналоги', title: 'Справочник аналогов ЯНАО' },
];

const defaultRange = (): DateRange => {
    const end = new Date();
    const start = new Date(end);
    start.setMonth(start.getMonth() - 1);

    return { start: toIsoDate(start), end: toIsoDate(end) };
};

const fileName = (kind: CatalogExportKind, range: DateRange | null): string => {
    const title = KINDS.find((item) => item.value === kind)?.title ?? 'Справочник';

    return range
        ? `${title} за период с ${formatRuDate(range.start)} по ${formatRuDate(range.end)}.xlsx`
        : `${title} на ${formatRuDate(toIsoDate(new Date()))}.xlsx`;
};

interface ExportCatalogModalProps {
    open: boolean;
    onClose: () => void;
    onNotify: (message: string) => void;
}

export const ExportCatalogModal: FC<ExportCatalogModalProps> = ({ open, onClose, onNotify }) => {
    const [kind, setKind] = useState<CatalogExportKind>('all');
    const [byPeriod, setByPeriod] = useState(false);
    const [range, setRange] = useState<DateRange>(defaultRange);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!open) return;
        setKind('all');
        setByPeriod(false);
        setRange(defaultRange());
        setIsLoading(false);
    }, [open]);

    const submit = async (): Promise<void> => {
        const period = byPeriod ? range : null;

        setIsLoading(true);

        try {
            await downloadFile(catalogExportPath(kind, period), fileName(kind, period));
            onClose();
        } catch (error) {
            onNotify(error instanceof Error ? error.message : 'Не удалось выгрузить справочник');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal
            open={open}
            width={440}
            title="Выгрузка справочника"
            description="Выберите вид отчёта. Без периода выгружается весь справочник за всё время."
            icon={<IconDownload />}
            onClose={onClose}
            footer={
                <>
                    <ModalNote>Формат файла — Excel</ModalNote>
                    <ModalActions>
                        <Button onClick={onClose} disabled={isLoading}>
                            Отмена
                        </Button>
                        <Button variant="primary" disabled={isLoading} onClick={submit}>
                            {isLoading ? 'Готовим файл' : 'Выгрузить'}
                        </Button>
                    </ModalActions>
                </>
            }
        >
            <div className={styles.form}>
                <div className={styles.field}>
                    <span className={styles.label}>Вид отчёта</span>
                    <div className={styles.kinds}>
                        {KINDS.map((item) => (
                            <button
                                key={item.value}
                                type="button"
                                className={cx(styles.kind, kind === item.value && styles.kindActive)}
                                aria-pressed={kind === item.value}
                                disabled={isLoading}
                                onClick={() => setKind(item.value)}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className={styles.field}>
                    <Switch
                        label="Ограничить периодом"
                        checked={byPeriod}
                        disabled={isLoading}
                        onChange={() => setByPeriod((value) => !value)}
                    />
                    {byPeriod && (
                        <DateRangePicker
                            value={range}
                            disabled={isLoading}
                            onChange={(next) => next && setRange(next)}
                        />
                    )}
                </div>
            </div>
        </Modal>
    );
};